// @givmo/ai — the ui_block renderer registry: one native React renderer per v1
// block type, plus the two §2.3 degrade cases folded into a plain markdown text
// bubble. Mirrors the iOS `AssistantBlockView` switch (`AssistantBlockViews.swift`)
// and `RenderBlock.init(uiBlock:)` (degrade → text). This is a THIN package: the
// renderers are unstyled, behavior-correct primitives; the product surface (08)
// composes + themes them via `@givmo/design-system`.
//
// Forward-compat (the crux — §2.3): the registry NEVER receives a raw wire block.
// It receives a `DecodedUiBlock` (the output of `decodeUiBlock`), which has already
// applied the "unknown type OR higher block_version → fallback_text" rule. So:
//   - `kind: 'supported'`  → the matching native renderer for `block.type`;
//   - `kind: 'unsupported'`→ render `fallback_text` as a text bubble;
//   - `kind: 'degraded_text'` → render the raw `text` as a text bubble.
// An unknown `block.type` can therefore never reach a `switch` default that throws
// — the only fallthrough renders text. Nothing here crashes the turn.
//
// Trust posture (§3): NO model-authored URLs. `action_chips` navigate ONLY via a
// `ValidatedRoute` resolved through `resolveWebUrl` (the route registry's web
// mapping); a route with no pinned web destination renders an INERT chip (no
// href), never an invented URL. `text`/`mission_snippet`/`crisis.message` render
// through `AssistantMarkdownText` (images + links disabled). `charity.logo_url` is
// the only model-adjacent URL rendered, and only as an `<img src>` (never a
// navigable link) — server-escaped + provenance-labeled upstream (§3.3).
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { ASSISTANT_EVENTS, type AssistantAnalyticsEmit } from '../analytics/events';
import type {
  ActionChip,
  ActionChipsBlock,
  CharityCard,
  CharityCardsBlock,
  ComparisonBlock,
  CrisisBlock,
  CrisisResource,
  DisclosureBlock,
  EscalationOfferBlock,
  KnownUiBlock,
  TextBlock,
} from '../contract/blocks';
import type { ValidatedRoute } from '../contract/routes';
import { resolveWebUrl, type GivmoEnv } from '../routes/web-route';
import { AssistantMarkdownText } from './markdown';
import type { DecodedUiBlock } from '../contract/decode';

/** Props common to the whole registry: deterministic env + optional seams. */
export interface BlockRenderContext {
  /** Selects the donate web host for chip URLs (mirrors backend `donate_web_host()`). */
  env: GivmoEnv;
  /** Structural analytics emit (no chat text). Renderers fire `ui_block_rendered`
   *  on mount and `chip_tapped` / `escalation_proposed` on interaction. Optional —
   *  the thin package works without analytics wired (08 injects the real emit). */
  emit?: AssistantAnalyticsEmit;
  /** Invoked when an escalation-offer block is tapped. The block itself sends
   *  NOTHING — this opens the existing user-confirmed handoff (an inert proposal,
   *  §3.1). */
  onEscalationOffer?: (block: EscalationOfferBlock) => void;
  /** Invoked when a chip with a resolvable web URL is tapped, BEFORE navigation,
   *  so the host can intercept (e.g. SPA client-side nav). When omitted, a chip
   *  with a URL renders as a real `<a href>`. */
  onChipNavigate?: (route: ValidatedRoute, url: string) => void;
}

// MARK: - Top-level renderer

/** Render one decoded ui_block. The degrade cases collapse to a text bubble; a
 * supported block dispatches to its native renderer. Never throws. */
export function BlockRenderer({
  decoded,
  ctx,
}: {
  decoded: DecodedUiBlock;
  ctx: BlockRenderContext;
}): ReactElement {
  if (decoded.kind === 'degraded_text') {
    return <AssistantMarkdownText text={decoded.text} testId="assistant-block-text" />;
  }
  if (decoded.kind === 'unsupported') {
    // Unknown type / over-version: render the schema-required fallback copy.
    return <AssistantMarkdownText text={decoded.fallback_text} testId="assistant-block-fallback" />;
  }
  return <SupportedBlock block={decoded.block} ctx={ctx} />;
}

/** Render an ordered list of decoded blocks (e.g. a finalized message's blocks). */
export function BlockListRenderer({
  blocks,
  ctx,
}: {
  blocks: readonly DecodedUiBlock[];
  ctx: BlockRenderContext;
}): ReactElement {
  return (
    <div data-testid="assistant-block-list">
      {blocks.map((decoded, i) => (
        <BlockRenderer key={i} decoded={decoded} ctx={ctx} />
      ))}
    </div>
  );
}

/** Dispatch a known v1 block to its native renderer (discriminated on `type`).
 * Fires the structural `ui_block_rendered` signal once for the block's type. */
function SupportedBlock({
  block,
  ctx,
}: {
  block: KnownUiBlock;
  ctx: BlockRenderContext;
}): ReactElement {
  useBlockRenderedAnalytics(block.type, ctx.emit);
  switch (block.type) {
    case 'text':
      return <TextBlockView block={block} />;
    case 'action_chips':
      return <ActionChipsView block={block} ctx={ctx} />;
    case 'charity_cards':
      return <CharityCardsView block={block} />;
    case 'comparison':
      return <ComparisonView block={block} />;
    case 'escalation_offer':
      return <EscalationOfferView block={block} ctx={ctx} />;
    case 'crisis':
      return <CrisisView block={block} />;
    case 'disclosure':
      return <DisclosureView block={block} />;
  }
}

/** Fire `ui_block_rendered` once on mount, structurally (block type only — never
 * chat text). A mount-effect so the signal fires exactly once per mounted block
 * instance, not on every re-render. */
function useBlockRenderedAnalytics(type: string, emit?: AssistantAnalyticsEmit): void {
  // Fire exactly once per mounted block instance (a structural mount signal), so
  // the dependency list is intentionally empty — the `emit`/`type` for a given
  // mounted block are fixed for its lifetime.
  useEffect(() => {
    emit?.({ name: ASSISTANT_EVENTS.uiBlockRendered, params: { type } });
  }, []);
}

// MARK: - .text

function TextBlockView({ block }: { block: TextBlock }): ReactElement {
  return <AssistantMarkdownText text={block.text} testId="assistant-block-text" />;
}

// MARK: - .action_chips

function ActionChipsView({
  block,
  ctx,
}: {
  block: ActionChipsBlock;
  ctx: BlockRenderContext;
}): ReactElement {
  return (
    <div data-testid="assistant-action-chips" role="group" aria-label={block.fallback_text}>
      {block.chips.map((chip, i) => (
        <ActionChipButton key={i} chip={chip} ctx={ctx} />
      ))}
    </div>
  );
}

/** One chip: navigates ONLY via a `ValidatedRoute` → `resolveWebUrl`. A route with
 * no pinned web URL renders an INERT chip (a disabled button, no href) — never an
 * invented URL (§3.2). A resolvable route renders an `<a href>` unless the host
 * intercepts via `onChipNavigate`. */
function ActionChipButton({
  chip,
  ctx,
}: {
  chip: ActionChip;
  ctx: BlockRenderContext;
}): ReactElement {
  const url = resolveWebUrl(chip.route, ctx.env);
  const routeType = String(chip.route.route_type);
  const testId = `assistant-chip-${routeType}`;

  // Inert: no pinned web destination → non-navigable (disabled button).
  if (url === null) {
    return (
      <button type="button" disabled data-testid={testId} data-inert="true">
        {chip.label}
      </button>
    );
  }

  const fire = () => {
    ctx.emit?.({ name: ASSISTANT_EVENTS.chipTapped, params: { route_type: routeType } });
  };

  // Host intercepts navigation (SPA client-side routing) → render a button.
  if (ctx.onChipNavigate) {
    return (
      <button
        type="button"
        data-testid={testId}
        data-href={url}
        onClick={() => {
          fire();
          ctx.onChipNavigate?.(chip.route, url);
        }}
      >
        {chip.label}
      </button>
    );
  }

  // Plain navigation: a real anchor to the server-validated URL.
  return (
    <a href={url} data-testid={testId} onClick={fire}>
      {chip.label}
    </a>
  );
}

// MARK: - .charity_cards / .comparison (shared card)

function CharityCardsView({ block }: { block: CharityCardsBlock }): ReactElement {
  return (
    <div data-testid="assistant-charity-cards">
      {block.cards.map((card, i) => (
        <CharityCardView key={i} card={card} />
      ))}
    </div>
  );
}

function ComparisonView({ block }: { block: ComparisonBlock }): ReactElement {
  return (
    <div data-testid="assistant-comparison">
      {block.dimensions.length > 0 && (
        <div data-testid="assistant-comparison-dimensions">
          {`Comparing ${block.dimensions.join(' · ')}`}
        </div>
      )}
      {block.cards.map((card, i) => (
        <CharityCardView key={i} card={card} />
      ))}
    </div>
  );
}

/** A single charity card. The logo renders as an `<img>` only (never a link); the
 * mission snippet renders through the link/image-disabled markdown renderer (it is
 * charity-supplied free text — §3.3). `sparkline` is decoded but not plotted in v1
 * (the thin package leaves charting to the 08 surface). */
function CharityCardView({ card }: { card: CharityCard }): ReactElement {
  return (
    <div data-testid={`assistant-charity-card-${card.charity_id}`}>
      {card.logo_url ? (
        <img src={card.logo_url} alt="" data-testid="assistant-charity-card-logo" />
      ) : null}
      <span data-testid="assistant-charity-card-name">{card.name}</span>
      {card.mission_snippet ? (
        <AssistantMarkdownText
          text={card.mission_snippet}
          testId="assistant-charity-card-mission"
        />
      ) : null}
      {card.standing_note ? (
        <span data-testid="assistant-charity-card-standing">{card.standing_note}</span>
      ) : null}
    </div>
  );
}

// MARK: - .escalation_offer

function EscalationOfferView({
  block,
  ctx,
}: {
  block: EscalationOfferBlock;
  ctx: BlockRenderContext;
}): ReactElement {
  return (
    <button
      type="button"
      data-testid="assistant-escalation-offer"
      onClick={() => {
        ctx.emit?.({ name: ASSISTANT_EVENTS.escalationProposed });
        ctx.onEscalationOffer?.(block);
      }}
    >
      <span data-testid="assistant-escalation-offer-title">Talk to a person</span>
      {block.summary ? (
        <span data-testid="assistant-escalation-offer-summary">{block.summary}</span>
      ) : null}
    </button>
  );
}

// MARK: - .crisis

function CrisisView({ block }: { block: CrisisBlock }): ReactElement {
  return (
    <div data-testid="assistant-crisis" role="note">
      <p data-testid="assistant-crisis-message">{block.message}</p>
      <ul>
        {block.resources.map((resource, i) => (
          <CrisisResourceRow key={i} resource={resource} />
        ))}
      </ul>
    </div>
  );
}

/** A crisis resource. Builds a `tel:`/`sms:` link from the contact digits when the
 * kind implies one; an unknown kind (or no parseable digits) renders inert text.
 * These are registry-derived `tel:`/`sms:` schemes, NOT model-authored web URLs. */
function CrisisResourceRow({ resource }: { resource: CrisisResource }): ReactElement {
  const href = crisisHref(resource);
  const body: ReactNode = (
    <>
      <span data-testid="assistant-crisis-resource-label">{resource.label}</span>
      <span data-testid="assistant-crisis-resource-contact">{resource.contact}</span>
    </>
  );
  return (
    <li data-testid="assistant-crisis-resource">
      {href ? (
        <a href={href} data-testid="assistant-crisis-resource-link">
          {body}
        </a>
      ) : (
        body
      )}
    </li>
  );
}

/** Build a `tel:`/`sms:` URL from a resource's contact digits, or `null`. Mirrors
 * iOS `contactURL(for:)`. */
function crisisHref(resource: CrisisResource): string | null {
  const digits = resource.contact.replace(/[^0-9+]/g, '');
  if (digits.length === 0) return null;
  switch (resource.kind) {
    case 'call':
    case 'emergency':
      return `tel:${digits}`;
    case 'text':
      return `sms:${digits}`;
    default:
      return null; // unknown future kind → inert
  }
}

// MARK: - .disclosure

function DisclosureView({ block }: { block: DisclosureBlock }): ReactElement {
  return (
    <div data-testid="assistant-disclosure" role="note">
      <span data-testid="assistant-disclosure-body">{block.body}</span>
      {block.policy_url ? (
        <a href={block.policy_url} data-testid="assistant-disclosure-policy">
          Privacy Policy
        </a>
      ) : null}
    </div>
  );
}
